import React from 'react'
import { useState, useMemo } from 'react'
import { OmDropdownMenu, type OmMenuItem } from '@owma/kit'
import i18n from 'i18n'
import styles from './EventEditor.module.scss'
import repeatIcon from 'assets/images/recurrence-24.svg'
import chevronDownIcon from 'assets/images/chevron-down-24.svg'
import { RRule, Weekday, Options as RRuleOptions } from 'rrule'
import RecurrenceEditor from './RecurrenceEditor'
import { rruleDescription } from '../../utils/RRuleDesc'

interface EditEventData {
  startDate: Date
  duration: number
  recurrenceOptions: Partial<RRuleOptions> | null
  isMyEvent: boolean
  setStartDate: (date: Date) => void
  setEndDate: (date: Date) => void
}

interface RecurrenceOptions {
  freq?: number
  interval?: number
  byweekday?: Weekday | Weekday[]
  bymonth?: number | number[]
  bymonthday?: number | number[]
  bysetpos?: number
  until?: Date | number | null
  count?: number | null
  dtstart?: Date
}

interface RecurrenceSelectorProps {
  editEventData: EditEventData
  onRecurrenceChange: (options: RecurrenceOptions, start?: Date) => void
  disabled?: boolean
}

interface RecurrenceOption {
  key: string
  label: string
  freq?: number
  interval?: number
}

const RecurrenceSelector: React.FC<RecurrenceSelectorProps> = ({
  editEventData,
  onRecurrenceChange,
  disabled = false,
}) => {
  const RECURRENCE_OPTIONS = useMemo<RecurrenceOption[]>(
    () => [
      { key: 'daily', label: i18n.t('calendar.recurrence.daily'), freq: RRule.DAILY, interval: 1 },
      {
        key: 'weekly',
        label: i18n.t('calendar.recurrence.weekly'),
        freq: RRule.WEEKLY,
        interval: 1,
      },
      {
        key: 'biweekly',
        label: i18n.t('calendar.recurrence.biweekly'),
        freq: RRule.WEEKLY,
        interval: 2,
      },
      {
        key: 'monthly',
        label: i18n.t('calendar.recurrence.monthly'),
        freq: RRule.MONTHLY,
        interval: 1,
      },
      {
        key: '3monthly',
        label: i18n.t('calendar.recurrence.every3months'),
        freq: RRule.MONTHLY,
        interval: 3,
      },
      {
        key: '6monthly',
        label: i18n.t('calendar.recurrence.every6months'),
        freq: RRule.MONTHLY,
        interval: 6,
      },
      {
        key: 'yearly',
        label: i18n.t('calendar.recurrence.yearly'),
        freq: RRule.YEARLY,
        interval: 1,
      },
      { key: 'custom', label: i18n.t('calendar.recurrence.customRecurrence') },
    ],
    [],
  )

  const [showEditor, setShowEditor] = useState(false)

  const handleRecurrenceChange = (key: string) => {
    if (key === 'custom') {
      setShowEditor(true)
      return
    }
    const options = RECURRENCE_OPTIONS.find((option) => option.key === key)
    if (!options) return

    const rruleOptions: RecurrenceOptions = {
      freq: options.freq,
      interval: options.interval,
    }

    const startDate = editEventData.startDate
    if (options.freq === RRule.DAILY) {
      let until = new Date(startDate)
      until.setMonth(startDate.getMonth() + 1)
      if (until.getMonth() !== startDate.getMonth() + 1) {
        until.setDate(0)
      }
      rruleOptions.until = until
    } else if (options.freq === RRule.WEEKLY) {
      let weekday = startDate.getDay() - 1
      if (weekday === -1) {
        weekday = 6
      }
      rruleOptions.byweekday = [new Weekday(weekday)]
      rruleOptions.until = new Date(startDate.getTime() + 7 * 12 * 24 * 60 * 60 * 1000)
    } else if (options.freq === RRule.MONTHLY) {
      rruleOptions.bymonthday = startDate.getDate()
      rruleOptions.until = new Date(new Date(startDate).setFullYear(startDate.getFullYear() + 1))
    } else if (options.freq === RRule.YEARLY) {
      rruleOptions.bymonthday = startDate.getDate()
      rruleOptions.bymonth = startDate.getMonth() + 1
    }
    onRecurrenceChange(rruleOptions)
  }

  const handleCustomSave = (options: RecurrenceOptions, startDate: Date | null) => {
    if (startDate && startDate.getTime() !== editEventData.startDate.getTime()) {
      editEventData.setStartDate(startDate)
      const duration = editEventData.duration
      editEventData.setEndDate(new Date(startDate.getTime() + duration * 60 * 1000))
    }
    onRecurrenceChange(options, startDate as any)
    setShowEditor(false)
  }

  const getRecurrenceItems = (): OmMenuItem[] => {
    return RECURRENCE_OPTIONS.map((option) => ({
      key: option.key,
      label: option.label,
      onClick: () => handleRecurrenceChange(option.key),
    }))
  }

  const getCurrentRecurrence = (): string => {
    const options = editEventData.recurrenceOptions
    if (!options) {
      return i18n.t('calendar.recurrence.never')
    }

    return rruleDescription(new RRule(options as RRuleOptions) as any)
  }

  const getButton = (dropdownDisabled = false) => {
    return (
      <button
        className={`${styles.recurrenceButton} ${disabled ? styles.disabled : ''}`}
        onClick={
          !dropdownDisabled && !disabled ? () => handleRecurrenceChange('custom') : undefined
        }
        style={disabled ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
      >
        <span>{getCurrentRecurrence()}</span>
        <img
          src={chevronDownIcon}
          alt="More"
          className={styles.chevronIcon}
          style={disabled ? { opacity: 0.5 } : {}}
        />
      </button>
    )
  }

  return (
    <div className={styles.recurrenceWrapper}>
      <img
        src={repeatIcon}
        alt="Recurrence"
        className={styles.titleIcon}
        style={disabled ? { opacity: 0.5 } : {}}
      />
      <div
        className={`${styles.recurrenceContent} ${!editEventData.isMyEvent ? styles.dimmed : ''}`}
      >
        {editEventData.recurrenceOptions ? (
          getButton()
        ) : (
          <OmDropdownMenu
            menu={{ items: getRecurrenceItems() }}
            trigger={['click']}
            placement="bottomLeft"
            disabled={disabled}
            overlayClassName={'contextMenu'}
          >
            {getButton(true)}
          </OmDropdownMenu>
        )}
      </div>

      {showEditor && (
        <RecurrenceEditor
          open={showEditor}
          onClose={() => setShowEditor(false)}
          onSave={handleCustomSave}
          startDate={new Date(editEventData.startDate)}
          options={editEventData.recurrenceOptions as any}
        />
      )}
    </div>
  )
}

export default RecurrenceSelector
